import { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@mui/material'

import InviteModal from '../../components/dataGrid/InviteModal.jsx'
import StaffDataGrid from '../../components/dataGrid/StaffDataGrid.jsx'
import { GeneralPaymentPopup } from '../../components/popups/GeneralPaymentPopup.jsx'
import { RemoveStaffPopup } from '../../components/popups/RemoveStaffPopup.jsx'
import ButtonCustom from '../../components/atoms/button/ButtonCustom.jsx'

import {
	getStaffInfo,
	inviteVendorStaffByEmails,
	updateStaffStatus,
} from '../../features/vendorSlice'
import { toastHelper } from '../../util/toastHelper.js'
import { useVendorOrPlannerSubscriptionAccess } from '../../hooks/index.js'

import styles from './InviteStaffPage.module.scss'

export const InviteStaffPage = () => {
	const dispatch = useDispatch()
	const staff = useSelector((s) => s.vendor.staff)
  	const subscriptionAccess = useVendorOrPlannerSubscriptionAccess()

	const [removing, setRemoving] = useState(false)
	const [selected, setSelected] = useState(null) // { email } for add, or staff object for remove
	const [showPaymentPopup, setShowPaymentPopup] = useState(false)
	
	const isBusinessPro = useMemo(() => ['business-pro', 'business-pro-month'].includes(subscriptionAccess?.payment), [subscriptionAccess?.payment])
	
	const toggleRemove = (obj) => {
		if (!removing) setSelected(obj)
		setRemoving(!removing)
	}

	useEffect(() => {
		dispatch(getStaffInfo())
	}, [dispatch])

	const handleAccept = (memberId) => {
		dispatch(acceptFriendRequest(memberId))
	}
	const handleStatusChange = async (status, row) => {
		const { staffId, type } = row
		if(!status || !staffId || !type) {
			toastHelper('Missing status or staff id', 'error')
			return
		}
		await dispatch(updateStaffStatus({staffId, status, type })).unwrap()
		dispatch(getStaffInfo())
	}

	const [inviteOpen, setInviteOpen] = useState(false)

	const openInvite = () =>
		isBusinessPro ? setInviteOpen(true) : setShowPaymentPopup(true)

	const handleSubmitStaff = async(rows) => {
		const emails = rows
			.map((r) =>
				String(r.email || '')
					.trim()
					.toLowerCase()
			)
			.filter(Boolean)

		if (!emails.length) {
			toastHelper('Please add at least one email', 'error')
			return
		}
		await dispatch(inviteVendorStaffByEmails(rows)).unwrap()
		setInviteOpen(false)
		dispatch(getStaffInfo())
	}

	return (
		<>
			<div className={styles.staffPageWrp}>
				<div className={styles.staffHeader}>
					<Typography component="h1">Staff</Typography>
					<div className={styles.right}>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={openInvite}
							data-cy="invite-members"
							className={styles.inviteBtn}
						>
							+ Invite staff
						</ButtonCustom>
					</div>
				</div>

				{removing && (
					<RemoveStaffPopup
						staff={selected}
						clickHandler={toggleRemove}
						open={removing}
						handleClose={toggleRemove}
					/>
				)}

				<StaffDataGrid
					staff={staff}
					handleStatusChange={handleStatusChange}
					onRemove={toggleRemove}
				/>

				<InviteModal
					type="staff"
					open={inviteOpen}
					onClose={() => setInviteOpen(false)}
					title="Invite staff by email"
					subtitle="Add staff members to your team (up to 10 at a time)"
					withRole={false}
					submitLabel="Send invite"
					onSubmit={handleSubmitStaff}
					roleLabel="Staff"
				/>

				{showPaymentPopup && (
					<GeneralPaymentPopup
						heading="Upgrade to Business Pro for add staffs"
						clickHandler={() => setShowPaymentPopup(false)}
					/>
				)}
			</div>
		</>
	)
}
